import styled from 'styled-components';
import { BusinessHours } from '@elfsight/widget-business-hours/widget';
import { Address } from '@elfsight/widget-ui';
import { useLingui } from '@lingui/react/macro';
import { isMobile as isMobileDevice } from 'react-device-detect';
import {
  ContactsAddressItem,
  ContactsEmailItem,
  ContactsInstagramItem,
  ContactsPhoneItem,
  ContactsStatusIndicator,
  ContactsWebsiteItem,
  ContactsWhatsAppItem
} from '../contacts/contacts';
import { CTAButtons } from '../cta-buttons';
import { useLocationCardProps } from '../location-card-settings-context';

export function Content({ withSchedule }: { withSchedule?: boolean }) {
  const settings = useLocationCardProps();
  const {
    businessLogo,
    businessName,
    businessAbout,
    ctaButtons,
    contactsShowIcons,
    contactsShowLabels,
    textStyle,
    textColor,
    iconsColor,
    contactsPhone,
    contactsEmail,
    contactsAddress,
    contactsWhatsApp,
    contactsInstagram,
    contactsLocationAddress,
    contactsLocationName,
    contactsWebsite,
    businessHoursDisplay
  } = settings;

  const { t } = useLingui();

  const hasBusinessInfo = !!businessLogo || !!businessName || !!businessAbout;

  const contactsItemProps = {
    showLabel: contactsShowLabels,
    showIcon: contactsShowIcons,
    textStyle,
    textColor,
    iconColor: iconsColor,
    withUnderline: isMobileDevice,
    cursor: 'pointer'
  };

  const regularBlocks = [
    {
      render: (
        <ContactsPhoneItem {...contactsItemProps} value={contactsPhone} />
      ),
      condition: !!settings.contactsPhone,
      offset: 16
    },
    {
      render: (
        <ContactsWhatsAppItem {...contactsItemProps} value={contactsWhatsApp} />
      ),
      condition: !!settings.contactsWhatsApp,
      offset: 16
    },
    {
      render: (
        <ContactsInstagramItem
          {...contactsItemProps}
          value={contactsInstagram}
        />
      ),
      condition: !!settings.contactsInstagram,
      offset: 16
    },
    {
      render: (
        <ContactsAddressItem
          {...contactsItemProps}
          value={
            <StyledAddress
              phrases={{
                open: t`Open in Google Maps`,
                getDirection: t`Get Directions`,
                copy: t`Copy`
              }}
              fontSize={textStyle?.fontSize}
              color={textColor}
              address={contactsAddress}
              locationAddress={contactsLocationAddress}
              name={typeof businessName === 'string' ? businessName : ''}
              locationName={contactsLocationName}
            />
          }
        />
      ),
      condition: !!settings.contactsAddress,
      offset: 16
    },
    {
      render: (
        <ContactsEmailItem {...contactsItemProps} value={contactsEmail} />
      ),
      condition: !!settings.contactsEmail,
      offset: 16
    },
    {
      render: (
        <ContactsWebsiteItem {...contactsItemProps} value={contactsWebsite} />
      ),
      condition: !!settings.contactsWebsite,
      offset: 16
    },
    {
      render: (
        <ContactsStatusIndicator
          {...contactsItemProps}
          cursor={businessHoursDisplay === 'onPage' ? 'default' : 'pointer'}
          value={
            <BusinessHours
              mode={
                businessHoursDisplay === 'onPage' ? 'status' : 'statusWithHours'
              }
              {...settings}
            />
          }
        />
      ),
      condition: businessHoursDisplay !== 'none',
      offset: 16
    },
    {
      render: <CTAButtons />,
      condition: !hasBusinessInfo && !!ctaButtons?.length,
      offset: 32
    },
    {
      render: <BusinessHours mode="hours" {...settings} />,
      condition: withSchedule && settings.businessHoursDisplay === 'onPage',
      offset: 32
    }
  ].filter(({ condition }) => !!condition);

  return (
    <>
      {regularBlocks.map(({ render, offset }, index) => (
        <Block _offset={offset} key={index}>
          {render}
        </Block>
      ))}
    </>
  );
}

const Block = styled.div<{ _offset: number }>`
  margin: ${({ _offset }) => _offset}px 0;
  line-height: 0;

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledAddress = styled(Address)`
  display: inline;
`;
